/**
 * ComplianceClient - Unified Compliance Orchestration
 *
 * Main integration point for casino platforms. Orchestrates geolocation
 * verification with customer risk profiling for complete compliance coverage.
 *
 * SDK is the primary data injection point for compliance verification.
 */

import { GeolocationClient } from '../geolocation/client';
import { RiskProfileClient } from '../risk-profile/client';
import type { VesantConfig, Logger, RequestOptions, RequiredVesantConfig, RequestInterceptor } from '../core/config';
import { ComplianceError, ValidationError, VesantError } from '../core/errors';
import { createConsoleLogger } from '../core/logger';
import type { JurisdictionConfig, LocationVerification, ValidateCipherTextResponse } from '../geolocation/types';
import type { CustomerProfile } from '../risk-profile/types';
import type {
  RegistrationVerificationRequest,
  RegistrationVerificationResponse,
  LoginVerificationRequest,
  LoginVerificationResponse,
  TransactionVerificationRequest,
  TransactionVerificationResponse,
  TransactionRiskResult,
  EventVerificationRequest,
  EventVerificationResponse,
  CurrencyRates,
  ComplianceLocationRequestInput,
  ComplianceLocationRequestResult,
} from './types';
import { DEFAULT_CURRENCY_RATES } from './types';
import type {
  LocationRequest,
  LocationRequestFilters,
  LocationRequestListResponse,
  PaginationParams,
} from '../geolocation/types';

export class ComplianceClient {
  private geoClient: GeolocationClient;
  private riskClient: RiskProfileClient;
  private config: RequiredVesantConfig;
  private logger: Logger;
  private currencyRates: CurrencyRates;
  private _currencyRatesCustomized: boolean = false;
  private _currencyRatesWarned: boolean = false;

  constructor(config: VesantConfig) {
    this.logger = config.logger || createConsoleLogger();
    const interceptors: RequestInterceptor[] = config.interceptors || [];
    this.config = {
      baseURL: config.baseURL,
      tenantId: config.tenantId,
      apiKey: config.apiKey || '',
      headers: config.headers || {},
      timeout: config.timeout || 10000,
      retries: config.retries || 3,
      debug: config.debug || false,
      autoCreateProfiles: config.autoCreateProfiles !== false, // default true
      syncMode: config.syncMode || 'sync',
      environment: config.environment,
      interceptors,
      logger: this.logger,
    };

    this.geoClient = new GeolocationClient({
      baseURL: this.config.baseURL,
      tenantId: this.config.tenantId,
      apiKey: this.config.apiKey,
      headers: this.config.headers,
      timeout: this.config.timeout,
      debug: this.config.debug,
      logger: this.logger,
      environment: this.config.environment,
    });

    this.riskClient = new RiskProfileClient({
      baseURL: this.config.baseURL,
      tenantId: this.config.tenantId,
      apiKey: this.config.apiKey,
      headers: this.config.headers,
      timeout: this.config.timeout,
      debug: this.config.debug,
      interceptors,
      logger: this.logger,
      environment: this.config.environment,
    });

    this.currencyRates = DEFAULT_CURRENCY_RATES;
  }

  // ============================================================================
  // Sub-Client Accessors
  // ============================================================================

  /** Get the underlying GeolocationClient for direct geolocation API access */
  getGeolocationClient(): GeolocationClient {
    return this.geoClient;
  }

  /** Get the underlying RiskProfileClient for direct risk profile API access */
  getRiskProfileClient(): RiskProfileClient {
    return this.riskClient;
  }

  // ============================================================================
  // Main Verification Methods
  // ============================================================================

  /**
   * Verify customer registration with automatic profile creation
   *
   * This is the primary integration point for new customer sign-ups.
   * Combines geolocation verification with customer risk profile creation.
   *
   * Important: Profile is only created if geolocation verification passes.
   * This prevents orphaned geolocation records when registration is blocked.
   *
   * @param request - Registration verification request
   * @returns Verification response with profile and compliance status
   *
   * @example
   * ```typescript
   * const result = await sdk.verifyAtRegistration({
   *   customerId: 'CUST-12345',
   *   fullName: 'John Doe',
   *   emailAddress: 'john@example.com',
   *   ipAddress: req.ip,
   *   deviceFingerprint: getDeviceFingerprint()
   * });
   *
   * if (result.allowed) {
   *   // Create account
   *   console.log('Profile created:', result.profile);
   *   if (result.requiresKYC) {
   *     // Redirect to KYC flow
   *   }
   * } else {
   *   // Block registration
   *   console.log('Blocked:', result.blockReasons);
   * }
   * ```
   */
  async verifyAtRegistration(
    request: RegistrationVerificationRequest,
    requestOptions?: RequestOptions
  ): Promise<RegistrationVerificationResponse> {
    const startTime = Date.now();

    // Validate required fields before making any API calls
    this.validateRegistrationRequest(request);

    let geoVerification: LocationVerification | null = null;
    let cipherTextResult: ValidateCipherTextResponse | undefined;

    try {
      if (this.config.debug) {
        this.logger.debug('Starting registration verification', { customerId: request.customerId });
      }

      // When cipherText is present, validate-ciphertext performs full geo verification
      // server-side. Calling verifyIP separately would create duplicate records/alerts.
      if (request.cipherText) {
        cipherTextResult = await this.executeCipherTextValidation(
          request.cipherText,
          request.customerId,
          'registration',
          request.ipAddress,
          {
            full_name: request.fullName,
            email: request.emailAddress,
            phone: request.phoneNumber,
            date_of_birth: request.dateOfBirth,
          },
          requestOptions
        );
        geoVerification = (cipherTextResult && this.buildLocationFromCipherText(cipherTextResult, request.ipAddress))
          ?? await this.geoClient.verifyIP({
              ip_address: request.ipAddress,
              user_id: request.customerId,
              event_type: 'registration',
              device_fingerprint: request.deviceFingerprint,
            }, requestOptions);
      } else {
        geoVerification = await this.geoClient.verifyIP({
          ip_address: request.ipAddress,
          user_id: request.customerId,
          event_type: 'registration',
          device_fingerprint: request.deviceFingerprint,
        }, requestOptions);
      }

      // Step 2: Check if registration should be blocked BEFORE creating profile
      // This prevents orphaned geo records and ensures atomic behavior
      const blockReasons = this.evaluateRegistrationBlock(geoVerification, cipherTextResult);

      if (blockReasons.length > 0) {
        if (this.config.debug) {
          this.logger.debug('Registration blocked at geo stage', { blockReasons });
        }

        // Return early without creating profile - prevents orphaned records
        return {
          allowed: false,
          geolocation: geoVerification,
          profile: null,
          requiresKYC: false,
          requiresEDD: false,
          blockReasons: blockReasons,
          processingTime: Date.now() - startTime,
          cipherTextValidation: cipherTextResult,
        };
      }

      // Step 3: Create customer risk profile (only if geo passed)
      const profile = await this.riskClient.createProfile({
        customer_id: request.customerId,
        entity_type: request.entityType || 'individual',
        customer_status: 'active',
        full_name: request.fullName,
        email_address: request.emailAddress,
        primary_phone_number: request.phoneNumber,
        date_of_birth: request.dateOfBirth,
        residential_address: request.address,
        country_of_residence: geoVerification.location.country_iso,

        // Geolocation data
        location: `${geoVerification.location.city}, ${geoVerification.location.country}`,
        location_compliance: geoVerification.is_compliant ? 'compliant' : 'non-compliant',

        // Initial KYC status
        kyc_status: 'pending',
      }, requestOptions);

      // Step 4: Determine final status and requirements
      const requiresKYC = geoVerification.jurisdiction?.require_kyc || false;
      const requiresEDD = geoVerification.jurisdiction?.require_enhanced_verification || false;

      if (this.config.debug) {
        this.logger.debug('Registration verification complete', {
          allowed: true,
          requiresKYC,
          requiresEDD,
        });
      }

      return {
        allowed: true,
        geolocation: geoVerification,
        profile: profile,
        requiresKYC: requiresKYC,
        requiresEDD: requiresEDD,
        blockReasons: [],
        processingTime: Date.now() - startTime,
        cipherTextValidation: cipherTextResult,
      };
    } catch (error) {
      if (this.config.debug) {
        this.logger.error('Registration verification failed', {
          code: error instanceof Error ? (error as any).code : undefined,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
      }

      // Preserve DUPLICATE_PROFILE errors so callers can distinguish
      // duplicate registrations from other failures
      if (error instanceof VesantError && error.code === 'DUPLICATE_PROFILE') {
        throw error;
      }

      throw new ComplianceError(
        'Registration verification failed',
        error instanceof Error ? error.message : undefined,
        geoVerification ? 'PROFILE_CREATION_FAILED' : 'GEO_VERIFICATION_FAILED'
      );
    }
  }

  /**
   * Evaluate if registration should be blocked based on geolocation verification
   *
   * Checks:
   * 1. General compliance status (is_compliant)
   * 2. Explicit block flag (is_blocked)
   * 3. Jurisdiction-specific registration allowance (allow_registration)
   * 4. Risk level thresholds
   *
   * @param geoVerification - Geolocation verification result
   * @returns Array of block reasons (empty if allowed)
   */
  private evaluateRegistrationBlock(
    geoVerification: LocationVerification,
    cipherTextResult?: ValidateCipherTextResponse
  ): string[] {
    const blockReasons: string[] = [];

    // Check if explicitly blocked
    if (geoVerification.is_blocked) {
      blockReasons.push(...(geoVerification.risk_reasons ?? []));
    }

    // Check general compliance
    if (!geoVerification.is_compliant) {
      if (!blockReasons.includes('non_compliant_jurisdiction')) {
        blockReasons.push('non_compliant_jurisdiction');
      }
    }

    // CRITICAL: Check if registration is specifically allowed in this jurisdiction
    // Some jurisdictions may allow login but not new registrations
    const jurisdiction = geoVerification.jurisdiction;
    if (jurisdiction) {
      if (jurisdiction.allow_registration === false) {
        blockReasons.push('registration_not_allowed_in_jurisdiction');
      }

      // Check jurisdiction status for registration-specific blocks
      if (jurisdiction.status === 'blocked' || jurisdiction.status === 'sanctioned') {
        if (!blockReasons.includes('blocked_jurisdiction')) {
          blockReasons.push('blocked_jurisdiction');
        }
      }

      // Restricted jurisdictions need explicit registration permission
      if (jurisdiction.status === 'restricted' && jurisdiction.allow_registration === false) {
        if (!blockReasons.includes('restricted_jurisdiction_no_registration')) {
          blockReasons.push('restricted_jurisdiction_no_registration');
        }
      }
    }

    // Check geofence rule evaluation
    if (geoVerification.geofence_evaluation?.blocked) {
      blockReasons.push(...(geoVerification.geofence_evaluation.reasons ?? []));
    }

    // Critical risk level should block registration
    if (geoVerification.risk_level === 'critical') {
      if (!blockReasons.includes('critical_risk_level')) {
        blockReasons.push('critical_risk_level');
      }
    }

    // CipherText-based blocks (only when cipherText was provided)
    if (cipherTextResult) {
      if (!cipherTextResult.valid) {
        blockReasons.push('ciphertext_validation_failed');
      }
      if (cipherTextResult.risk?.is_blocked) {
        blockReasons.push(...(cipherTextResult.risk.block_reasons || []));
      }
      if (cipherTextResult.risk?.location_mismatch) {
        blockReasons.push('gps_ip_location_mismatch');
      }
    }

    // GPS required but no cipherText provided — block
    if (geoVerification.gps_required && !cipherTextResult) {
      blockReasons.push('gps_verification_required');
    }

    return [...new Set(blockReasons)]; // Deduplicate
  }

  /**
   * Validate registration request has all required fields
   *
   * @param request - Registration request to validate
   * @throws ValidationError if required fields are missing or invalid
   */
  private validateRegistrationRequest(request: RegistrationVerificationRequest): void {
    const errors: string[] = [];

    if (!request.customerId?.trim()) {
      errors.push('customerId is required');
    }

    if (!request.ipAddress?.trim()) {
      errors.push('ipAddress is required');
    } else if (!this.isValidIP(request.ipAddress)) {
      errors.push('ipAddress format is invalid');
    }

    if (!request.emailAddress?.trim()) {
      errors.push('emailAddress is required');
    } else if (!this.isValidEmail(request.emailAddress)) {
      errors.push('emailAddress format is invalid');
    }

    if (!request.fullName?.trim()) {
      errors.push('fullName is required');
    }

    if (errors.length > 0) {
      throw new ValidationError(`Invalid registration request: ${errors.join(', ')}`, errors);
    }
  }

  /**
   * Validate IP address format (IPv4 or IPv6)
   */
  private isValidIP(ip: string): boolean {
    // IPv4 pattern
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    // Simplified IPv6 pattern (covers most common cases)
    const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::(?:[0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4}$|^(?:[0-9a-fA-F]{1,4}:){1,7}:$|^(?:[0-9a-fA-F]{1,4}:){0,6}::(?:[0-9a-fA-F]{1,4}:){0,5}[0-9a-fA-F]{1,4}$/;

    return ipv4Regex.test(ip) || ipv6Regex.test(ip);
  }

  /**
   * Validate email address format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private validateLoginRequest(request: LoginVerificationRequest): void {
    const errors: string[] = [];

    if (!request.customerId?.trim()) {
      errors.push('customerId is required');
    }

    if (!request.ipAddress?.trim()) {
      errors.push('ipAddress is required');
    } else if (!this.isValidIP(request.ipAddress)) {
      errors.push('ipAddress format is invalid');
    }

    if (errors.length > 0) {
      throw new ValidationError(`Invalid login request: ${errors.join(', ')}`, errors);
    }
  }

  private validateTransactionRequest(request: TransactionVerificationRequest): void {
    const errors: string[] = [];

    if (!request.customerId?.trim()) {
      errors.push('customerId is required');
    }

    if (!request.ipAddress?.trim()) {
      errors.push('ipAddress is required');
    } else if (!this.isValidIP(request.ipAddress)) {
      errors.push('ipAddress format is invalid');
    }

    if (request.amount <= 0) {
      errors.push('amount must be greater than 0');
    }

    if (!request.currency?.trim()) {
      errors.push('currency is required');
    }

    if (errors.length > 0) {
      throw new ValidationError(`Invalid transaction request: ${errors.join(', ')}`, errors);
    }
  }

  private validateEventRequest(request: EventVerificationRequest): void {
    const errors: string[] = [];

    if (!request.customerId?.trim()) {
      errors.push('customerId is required');
    }

    if (!request.ipAddress?.trim()) {
      errors.push('ipAddress is required');
    } else if (!this.isValidIP(request.ipAddress)) {
      errors.push('ipAddress format is invalid');
    }

    if (!request.eventType?.trim()) {
      errors.push('eventType is required');
    }

    if (errors.length > 0) {
      throw new ValidationError(`Invalid event request: ${errors.join(', ')}`, errors);
    }
  }

  /**
   * Verify customer login with profile activity update
   *
   * Verifies geolocation and updates customer profile with latest activity.
   *
   * @param request - Login verification request
   * @returns Verification response with compliance status
   *
   * @example
   * ```typescript
   * const result = await sdk.verifyAtLogin({
   *   customerId: 'CUST-12345',
   *   ipAddress: req.ip,
   *   deviceFingerprint: getDeviceFingerprint()
   * });
   *
   * if (result.allowed) {
   *   // Allow login
   *   if (result.requiresStepUp) {
   *     // Trigger MFA or additional verification
   *   }
   * } else {
   *   // Block login
   *   console.log('Blocked:', result.blockReasons);
   * }
   * ```
   */
  async verifyAtLogin(
    request: LoginVerificationRequest,
    requestOptions?: RequestOptions
  ): Promise<LoginVerificationResponse> {
    const startTime = Date.now();

    this.validateLoginRequest(request);

    try {
      if (this.config.debug) {
        this.logger.debug('Starting login verification', { customerId: request.customerId });
      }

      // Step 1: Verify geolocation, cipherText, and fetch profile in parallel.
      // When cipherText is present, validate-ciphertext performs full geo verification
      // server-side — calling verifyIP separately would create duplicate records/alerts.
      let cipherTextResult: ValidateCipherTextResponse | undefined;
      let geoVerification: LocationVerification;
      let profileResult: CustomerProfile | null;

      if (request.cipherText) {
        [cipherTextResult, profileResult] = await Promise.all([
          this.executeCipherTextValidation(
            request.cipherText, request.customerId, 'login', request.ipAddress,
            undefined, requestOptions
          ),
          this.riskClient.getProfile(request.customerId, requestOptions).catch(() => null),
        ]);
        geoVerification = (cipherTextResult && this.buildLocationFromCipherText(cipherTextResult, request.ipAddress))
          ?? await this.geoClient.verifyIP({
              ip_address: request.ipAddress,
              user_id: request.customerId,
              event_type: 'login',
              device_fingerprint: request.deviceFingerprint,
            }, requestOptions);
      } else {
        [, geoVerification, profileResult] = await Promise.all([
          Promise.resolve(undefined),
          this.geoClient.verifyIP({
            ip_address: request.ipAddress,
            user_id: request.customerId,
            event_type: 'login',
            device_fingerprint: request.deviceFingerprint,
          }, requestOptions),
          this.riskClient.getProfile(request.customerId, requestOptions).catch(() => null),
        ]);
      }

      // Step 2: Check if login should be blocked BEFORE creating/updating profile
      const loginBlockReasons = this.getBlockReasons(geoVerification, profileResult, cipherTextResult);
      const isBlocked =
        !geoVerification.is_compliant ||
        geoVerification.is_blocked ||
        !!(cipherTextResult?.risk?.is_blocked) ||
        cipherTextResult?.valid === false ||
        (geoVerification.gps_required && !cipherTextResult);

      if (isBlocked && !profileResult) {
        // Blocked and no existing profile — return without creating one
        return {
          allowed: false,
          geolocation: geoVerification,
          profile: null,
          requiresStepUp: false,
          blockReasons: loginBlockReasons,
          processingTime: Date.now() - startTime,
          cipherTextValidation: cipherTextResult,
        };
      }

      // Step 3: Get or create profile (only reached if not blocked, or profile already exists)
      let profile: CustomerProfile;
      if (profileResult) {
        profile = profileResult;

        // Update last activity and location
        if (!isBlocked && this.shouldUpdateProfile(profile, geoVerification.location.city)) {
          profile = await this.riskClient.updateProfile(profile.id, {
            last_recorded_activity: new Date().toISOString(),
            location: `${geoVerification.location.city}, ${geoVerification.location.country}`,
            location_compliance: geoVerification.is_compliant ? 'compliant' : 'non-compliant',
          }, requestOptions);
        }
      } else {
        // Profile doesn't exist - this shouldn't happen for login
        if (this.config.autoCreateProfiles && this.config.debug) {
          this.logger.warn('Profile not found for login, creating...');
        }
        profile = await this.createProfileFromGeo(request.customerId, geoVerification);
      }

      const requiresStepUp =
        geoVerification.risk_level === 'high' ||
        geoVerification.risk_level === 'critical' ||
        (cipherTextResult?.risk?.location_mismatch === true);

      return {
        allowed: !isBlocked && profile.customer_status !== 'suspended',
        geolocation: geoVerification,
        profile: profile,
        requiresStepUp: requiresStepUp,
        blockReasons: isBlocked || profile.customer_status === 'suspended'
          ? this.getBlockReasons(geoVerification, profile, cipherTextResult)
          : [],
        processingTime: Date.now() - startTime,
        cipherTextValidation: cipherTextResult,
      };
    } catch (error) {
      throw new ComplianceError('Login verification failed', error instanceof Error ? error.message : undefined);
    }
  }

  /**
   * Verify transaction with amount-based risk assessment
   *
   * Combines geolocation verification with transaction amount analysis
   * and customer risk profile for comprehensive transaction screening.
   *
   * @param request - Transaction verification request
   * @returns Verification response with transaction risk assessment
   *
   * @example
   * ```typescript
   * const result = await sdk.verifyAtTransaction({
   *   customerId: 'CUST-12345',
   *   ipAddress: req.ip,
   *   amount: 5000,
   *   currency: 'USD',
   *   transactionType: 'withdrawal',
   *   deviceFingerprint: getDeviceFingerprint()
   * });
   *
   * if (result.allowed) {
   *   // Process transaction
   * } else if (result.requiresApproval) {
   *   // Queue for manual review
   * } else {
   *   // Block transaction
   *   console.log('Blocked:', result.blockReasons);
   * }
   * ```
   */
  async verifyAtTransaction(
    request: TransactionVerificationRequest,
    requestOptions?: RequestOptions
  ): Promise<TransactionVerificationResponse> {
    const startTime = Date.now();

    this.validateTransactionRequest(request);

    try {
      if (this.config.debug) {
        this.logger.debug('Starting transaction verification', {
          customerId: request.customerId,
          amount: request.amount,
          currency: request.currency,
        });
      }

      // Step 1: Verify geolocation, cipherText, and fetch profile in parallel.
      // When cipherText is present, validate-ciphertext performs full geo verification
      // server-side — calling verifyIP separately would create duplicate records/alerts.
      let cipherTextResult: ValidateCipherTextResponse | undefined;
      let geoVerification: LocationVerification;
      let profileResult: CustomerProfile | null;

      if (request.cipherText) {
        [cipherTextResult, profileResult] = await Promise.all([
          this.executeCipherTextValidation(
            request.cipherText, request.customerId, 'transaction', request.ipAddress,
            undefined, requestOptions
          ),
          this.riskClient.getProfile(request.customerId, requestOptions).catch(() => null),
        ]);
        geoVerification = (cipherTextResult && this.buildLocationFromCipherText(cipherTextResult, request.ipAddress))
          ?? await this.geoClient.verifyIP({
              ip_address: request.ipAddress,
              user_id: request.customerId,
              event_type: 'transaction',
              device_fingerprint: request.deviceFingerprint,
            }, requestOptions);
      } else {
        [, geoVerification, profileResult] = await Promise.all([
          Promise.resolve(undefined),
          this.geoClient.verifyIP({
            ip_address: request.ipAddress,
            user_id: request.customerId,
            event_type: 'transaction',
            device_fingerprint: request.deviceFingerprint,
          }, requestOptions),
          this.riskClient.getProfile(request.customerId, requestOptions).catch(() => null),
        ]);
      }

      // Step 2: Check if blocked before proceeding with risk calculation
      const geoBlocked =
        !geoVerification.is_compliant ||
        geoVerification.is_blocked ||
        !!(cipherTextResult?.risk?.is_blocked) ||
        cipherTextResult?.valid === false ||
        (geoVerification.gps_required && !cipherTextResult);

      if (geoBlocked && !profileResult) {
        // Blocked and no profile — return early without risk calculation
        return {
          allowed: false,
          geolocation: geoVerification,
          profile: null,
          transactionRisk: { score: 0, level: 'low' as const, factors: [], allowed: false, requiresManualReview: false },
          requiresApproval: false,
          blockReasons: this.getTransactionBlockReasons(
            geoVerification,
            { score: 0, level: 'low', factors: [], allowed: false, requiresManualReview: false },
            true,
            cipherTextResult
          ),
          processingTime: Date.now() - startTime,
          cipherTextValidation: cipherTextResult,
        };
      }

      if (!profileResult) {
        throw new ComplianceError('Customer profile not found', request.customerId);
      }
      const profile = profileResult;

      // Step 3: Calculate transaction risk
      const transactionRisk = this.calculateTransactionRisk(
        request.amount,
        request.currency,
        geoVerification,
        profile,
        cipherTextResult
      );

      // Step 4: Check jurisdiction limits
      const jurisdictionAllowed = this.checkJurisdictionLimits(
        request.amount,
        request.currency,
        geoVerification.jurisdiction
      );

      // Step 5: Determine if transaction is allowed
      const isAllowed =
        !geoBlocked &&
        jurisdictionAllowed &&
        transactionRisk.allowed &&
        profile.customer_status !== 'suspended';

      return {
        allowed: isAllowed,
        geolocation: geoVerification,
        profile: profile,
        transactionRisk: transactionRisk,
        requiresApproval: transactionRisk.requiresManualReview,
        blockReasons: this.getTransactionBlockReasons(
          geoVerification, transactionRisk, jurisdictionAllowed, cipherTextResult
        ),
        processingTime: Date.now() - startTime,
        cipherTextValidation: cipherTextResult,
      };
    } catch (error) {
      throw new ComplianceError('Transaction verification failed', error instanceof Error ? error.message : undefined);
    }
  }

  /**
   * Generic event verification (for other touchpoints)
   *
   * @param request - Event verification request
   * @returns Verification response
   */
  async verifyEvent(request: EventVerificationRequest, requestOptions?: RequestOptions): Promise<EventVerificationResponse> {
    const startTime = Date.now();

    this.validateEventRequest(request);

    // When cipherText is present, validate-ciphertext performs full geo verification
    // server-side — calling verifyIP separately would create duplicate records/alerts.
    let cipherTextResult: ValidateCipherTextResponse | undefined;
    let geoVerification: LocationVerification;

    if (request.cipherText) {
      cipherTextResult = await this.executeCipherTextValidation(
        request.cipherText, request.customerId, request.eventType, request.ipAddress,
        undefined, requestOptions
      );
      geoVerification = (cipherTextResult && this.buildLocationFromCipherText(cipherTextResult, request.ipAddress))
        ?? await this.geoClient.verifyIP({
            ip_address: request.ipAddress,
            user_id: request.customerId,
            event_type: request.eventType,
            device_fingerprint: request.deviceFingerprint,
          }, requestOptions);
    } else {
      geoVerification = await this.geoClient.verifyIP({
        ip_address: request.ipAddress,
        user_id: request.customerId,
        event_type: request.eventType,
        device_fingerprint: request.deviceFingerprint,
      }, requestOptions);
    }

    // Optionally update profile activity
    if (this.config.autoCreateProfiles) {
      try {
        const profile = await this.riskClient.getProfile(request.customerId, requestOptions);
        await this.riskClient.updateProfile(profile.id, {
          last_recorded_activity: new Date().toISOString(),
        }, requestOptions);
      } catch (error) {
        // Profile doesn't exist, ignore or create based on config
        if (this.config.debug) {
          this.logger.warn('Profile not found for event');
        }
      }
    }

    const cipherTextBlocked = cipherTextResult
      ? (!cipherTextResult.valid || cipherTextResult.risk?.is_blocked === true)
      : false;

    const blockReasons = [...(geoVerification.risk_reasons ?? [])];
    if (cipherTextResult) {
      if (!cipherTextResult.valid) {
        blockReasons.push('ciphertext_validation_failed');
      }
      if (cipherTextResult.risk?.is_blocked) {
        blockReasons.push(...(cipherTextResult.risk.block_reasons || []));
      }
    }

    // GPS required but no cipherText provided — block
    const gpsBlocked = geoVerification.gps_required && !cipherTextResult;
    if (gpsBlocked) {
      blockReasons.push('gps_verification_required');
    }

    return {
      allowed: geoVerification.is_compliant && !geoVerification.is_blocked && !cipherTextBlocked && !gpsBlocked,
      geolocation: geoVerification,
      blockReasons: [...new Set(blockReasons)],
      processingTime: Date.now() - startTime,
      cipherTextValidation: cipherTextResult,
    };
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private shouldUpdateProfile(profile: CustomerProfile, newCity: string): boolean {
    // Update if location changed significantly
    return !profile.location || !profile.location.includes(newCity);
  }

  private async createProfileFromGeo(
    customerId: string,
    geoVerification: LocationVerification,
    customerData?: {
      fullName?: string;
      email?: string;
      phone?: string;
      dateOfBirth?: string;
    }
  ): Promise<CustomerProfile> {
    // Warn if creating profile without proper customer data
    if (!customerData?.email || !customerData?.fullName) {
      this.logger.warn(
        'Creating profile with incomplete data. ' +
        'Profiles should be created during registration with complete customer information.'
      );
    }

    return this.riskClient.createProfile({
      customer_id: customerId,
      entity_type: 'individual',
      customer_status: 'active',
      full_name: customerData?.fullName || undefined,
      email_address: customerData?.email || '',
      primary_phone_number: customerData?.phone || undefined,
      date_of_birth: customerData?.dateOfBirth || undefined,
      location: `${geoVerification.location.city}, ${geoVerification.location.country}`,
      location_compliance: geoVerification.is_compliant ? 'compliant' : 'non-compliant',
      country_of_residence: geoVerification.location.country_iso,
      kyc_status: 'pending',
    });
  }

  /**
   * Execute cipherText validation with graceful degradation.
   * Returns undefined if cipherText is not provided or validation fails.
   */
  private async executeCipherTextValidation(
    cipherText: string | undefined,
    userId: string,
    eventType: 'registration' | 'login' | 'transaction' | string,
    ipAddress: string,
    customerData?: {
      full_name?: string;
      email?: string;
      phone?: string;
      date_of_birth?: string;
    },
    requestOptions?: RequestOptions
  ): Promise<ValidateCipherTextResponse | undefined> {
    if (!cipherText) return undefined;

    try {
      return await this.geoClient.validateCipherText(
        cipherText,
        userId,
        eventType as any,
        ipAddress,
        customerData,
        requestOptions
      );
    } catch (err) {
      this.logger.warn('CipherText validation failed, proceeding with IP-only', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      return undefined;
    }
  }

  /**
   * Build a LocationVerification from a ValidateCipherTextResponse.
   * The validate-ciphertext endpoint now returns the full geo-verification data
   * (is_compliant, jurisdiction, geofence_evaluation, record_id, gps_required),
   * so a separate verifyIP call is unnecessary.
   */
  private buildLocationFromCipherText(ct: ValidateCipherTextResponse, ipAddress: string): LocationVerification | null {
    const loc = ct.location;
    const risk = ct.risk;
    if (!risk) return null;

    return {
      ip_address: ct.ip_address || ipAddress,
      location: {
        country: loc?.country ?? '',
        country_iso: loc?.country_iso ?? '',
        city: loc?.city ?? '',
        region: loc?.region ?? '',
        postal_code: '',
        latitude: loc?.latitude ?? 0,
        longitude: loc?.longitude ?? 0,
        timezone: '',
        is_vpn: risk.is_vpn,
        is_proxy: risk.is_proxy,
        is_tor: risk.is_tor,
        is_hosting: risk.is_hosting ?? false,
        is_anonymizer: risk.is_vpn || risk.is_proxy || risk.is_tor,
      },
      is_compliant: ct.is_compliant ?? !risk.is_blocked,
      is_blocked: risk.is_blocked,
      risk_level: risk.level,
      risk_score: risk.score,
      risk_reasons: risk.is_blocked && risk.block_reasons
        ? risk.block_reasons
        : (risk.factors ?? []),
      jurisdiction: ct.jurisdiction,
      geofence_evaluation: ct.geofence_evaluation,
      record_id: ct.record_id ?? '',
      gps_required: ct.gps_required,
    };
  }

  private calculateTransactionRisk(
    amount: number,
    currency: string,
    geoVerification: LocationVerification,
    profile: CustomerProfile,
    cipherTextResult?: ValidateCipherTextResponse
  ): TransactionRiskResult {
    if (!this._currencyRatesCustomized && !this._currencyRatesWarned) {
      this._currencyRatesWarned = true;
      this.logger.warn(
        'Using default currency exchange rates. ' +
        'Call updateCurrencyRates() or fetchCurrencyRates() for accurate transaction risk calculation.'
      );
    }

    let riskScore = 0;
    const factors: string[] = [];

    // Amount-based risk (normalized to USD)
    const normalizedAmount = this.normalizeToUSD(amount, currency);
    if (normalizedAmount > 10000) {
      riskScore += 30;
      factors.push('high_transaction_amount');
    } else if (normalizedAmount > 5000) {
      riskScore += 15;
      factors.push('elevated_transaction_amount');
    }

    // Geo risk
    riskScore += geoVerification.risk_score * 0.4;
    if (geoVerification.risk_level === 'high' || geoVerification.risk_level === 'critical') {
      factors.push('high_risk_location');
    }

    // Profile risk
    riskScore += profile.risk_score * 0.3;
    if (profile.risk_category === 'high' || profile.risk_category === 'critical') {
      factors.push('high_risk_customer');
    }

    // VPN/anonymizer during transaction
    if (geoVerification.location.is_vpn || geoVerification.location.is_proxy) {
      riskScore += 20;
      factors.push('anonymization_detected');
    }

    // Suspended account
    if (profile.customer_status === 'suspended') {
      riskScore += 50;
      factors.push('account_suspended');
    }

    // CipherText risk factors
    if (cipherTextResult) {
      if (cipherTextResult.risk?.location_mismatch) {
        riskScore += 20;
        factors.push('gps_ip_location_mismatch');
      }
      if (cipherTextResult.risk?.score) {
        riskScore += cipherTextResult.risk.score * 0.2;
      }
    }

    return {
      score: Math.min(riskScore, 100),
      level: this.getRiskLevel(riskScore),
      factors: factors,
      allowed: riskScore < 70,
      requiresManualReview: riskScore >= 60 && riskScore < 70,
    };
  }

  private checkJurisdictionLimits(
    amount: number,
    currency: string,
    jurisdiction?: JurisdictionConfig
  ): boolean {
    if (!jurisdiction || !jurisdiction.max_transaction_amount) {
      return true;
    }

    const normalizedAmount = this.normalizeToUSD(amount, currency);
    return normalizedAmount <= jurisdiction.max_transaction_amount;
  }

  private normalizeToUSD(amount: number, currency: string): number {
    const rate = this.currencyRates[currency.toUpperCase()] || 1;
    return amount * rate;
  }

  private getRiskLevel(score: number): 'low' | 'medium' | 'high' | 'critical' {
    if (score >= 80) return 'critical';
    if (score >= 60) return 'high';
    if (score >= 40) return 'medium';
    return 'low';
  }

  private getBlockReasons(
    geoVerification: LocationVerification,
    profile: CustomerProfile | null,
    cipherTextResult?: ValidateCipherTextResponse
  ): string[] {
    const reasons: string[] = [];

    if (geoVerification.is_blocked) {
      reasons.push(...(geoVerification.risk_reasons ?? []));
    }

    if (profile) {
      if (profile.customer_status === 'suspended') {
        reasons.push('account_suspended');
      }

      if (profile.has_sanctions) {
        reasons.push('sanctions_match');
      }
    }

    if (cipherTextResult) {
      if (!cipherTextResult.valid) {
        reasons.push('ciphertext_validation_failed');
      }
      if (cipherTextResult.risk?.is_blocked) {
        reasons.push(...(cipherTextResult.risk.block_reasons || []));
      }
    }

    // GPS required but no cipherText provided — block
    if (geoVerification.gps_required && !cipherTextResult) {
      reasons.push('gps_verification_required');
    }

    return [...new Set(reasons)];
  }

  private getTransactionBlockReasons(
    geoVerification: LocationVerification,
    transactionRisk: TransactionRiskResult,
    jurisdictionAllowed: boolean,
    cipherTextResult?: ValidateCipherTextResponse
  ): string[] {
    const reasons: string[] = [];

    if (!geoVerification.is_compliant) {
      reasons.push('non_compliant_jurisdiction');
    }

    if (!jurisdictionAllowed) {
      reasons.push('exceeds_jurisdiction_limit');
    }

    if (!transactionRisk.allowed) {
      reasons.push(...transactionRisk.factors);
    }

    if (cipherTextResult) {
      if (!cipherTextResult.valid) {
        reasons.push('ciphertext_validation_failed');
      }
      if (cipherTextResult.risk?.is_blocked) {
        reasons.push(...(cipherTextResult.risk.block_reasons || []));
      }
    }

    // GPS required but no cipherText provided — block
    if (geoVerification.gps_required && !cipherTextResult) {
      reasons.push('gps_verification_required');
    }

    return [...new Set(reasons)];
  }

  // ============================================================================
  // Location Request Methods
  // ============================================================================

  /**
   * Request live location from a customer
   *
   * Creates a location request and sends a notification to the customer
   * via the specified channel (SMS, email, or push). The customer receives
   * a link to share their location.
   *
   * @param input - Location request details
   * @returns Location request result with share link
   *
   * @example
   * ```typescript
   * // Request location via SMS for transaction verification
   * const result = await sdk.requestCustomerLocation({
   *   customerId: 'CUST-12345',
   *   channel: 'sms',
   *   phone: '+1234567890',
   *   reason: 'Verification required for high-value transaction'
   * });
   *
   * console.log('Share link sent:', result.shareLink);
   * console.log('Expires at:', result.tokenExpiry);
   *
   * // Request location via email
   * const emailResult = await sdk.requestCustomerLocation({
   *   customerId: 'CUST-12345',
   *   channel: 'email',
   *   email: 'customer@example.com',
   *   reason: 'Account verification',
   *   expiryHours: 48
   * });
   * ```
   */
  async requestCustomerLocation(
    input: ComplianceLocationRequestInput,
    requestOptions?: RequestOptions
  ): Promise<ComplianceLocationRequestResult> {
    if (this.config.debug) {
      this.logger.debug('Creating location request for customer', { customerId: input.customerId });
    }

    // Validate input
    this.validateLocationRequestInput(input);

    // Get customer profile if available
    let profile: CustomerProfile | undefined;
    try {
      profile = await this.riskClient.getProfile(input.customerId, requestOptions);
    } catch (error) {
      if (this.config.debug) {
        this.logger.warn('Customer profile not found', { customerId: input.customerId });
      }
    }

    // Use profile data if contact info not provided
    const phone = input.phone || profile?.primary_phone_number;
    const email = input.email || profile?.email_address;

    // Create the location request
    const result = await this.geoClient.createLocationRequest({
      user_id: input.customerId,
      channel: input.channel,
      reason: input.reason,
      phone: phone,
      email: email,
      expiry_hours: input.expiryHours,
    }, requestOptions);

    if (this.config.debug) {
      this.logger.debug('Location request created', {
        requestId: result.request.id,
        channel: input.channel,
        expiresAt: result.token_expiry,
      });
    }

    return {
      request: result.request,
      shareLink: result.share_link,
      tokenExpiry: result.token_expiry,
      profile: profile,
    };
  }

  /**
   * Get a specific location request by ID
   *
   * @param requestId - Location request ID
   * @returns Location request details
   *
   * @example
   * ```typescript
   * const request = await sdk.getLocationRequest('req_abc123');
   * console.log('Status:', request.status);
   * if (request.latitude && request.longitude) {
   *   console.log('Location captured:', request.latitude, request.longitude);
   * }
   * ```
   */
  async getLocationRequest(requestId: string, requestOptions?: RequestOptions): Promise<LocationRequest> {
    return this.geoClient.getLocationRequest(requestId, requestOptions);
  }

  /**
   * List location requests with filters and pagination
   *
   * @param filters - Optional filters
   * @param pagination - Optional pagination
   * @returns Paginated list of location requests
   *
   * @example
   * ```typescript
   * // Get pending requests for a customer
   * const pending = await sdk.listLocationRequests(
   *   { status: 'pending', user_id: 'CUST-12345' },
   *   { page: 1, limit: 20 }
   * );
   *
   * // Get all completed requests from last week
   * const completed = await sdk.listLocationRequests({
   *   status: 'completed',
   *   date_from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
   * });
   * ```
   */
  async listLocationRequests(
    filters?: LocationRequestFilters,
    pagination?: PaginationParams,
    requestOptions?: RequestOptions
  ): Promise<LocationRequestListResponse> {
    return this.geoClient.listLocationRequests(filters, pagination, requestOptions);
  }

  /**
   * Cancel a pending location request
   *
   * @param requestId - Location request ID
   *
   * @example
   * ```typescript
   * await sdk.cancelLocationRequest('req_abc123');
   * ```
   */
  async cancelLocationRequest(requestId: string, requestOptions?: RequestOptions): Promise<void> {
    if (this.config.debug) {
      this.logger.debug('Cancelling location request', { requestId });
    }
    await this.geoClient.cancelLocationRequest(requestId, requestOptions);
  }

  /**
   * Resend notification for a location request
   *
   * @param requestId - Location request ID
   * @param contact - Contact details (email or phone)
   *
   * @example
   * ```typescript
   * // Resend to a different phone number
   * await sdk.resendLocationRequest('req_abc123', { phone: '+0987654321' });
   *
   * // Resend to email
   * await sdk.resendLocationRequest('req_abc123', { email: 'new@example.com' });
   * ```
   */
  async resendLocationRequest(
    requestId: string,
    contact: { email?: string; phone?: string },
    requestOptions?: RequestOptions
  ): Promise<void> {
    if (this.config.debug) {
      this.logger.debug('Resending location request', { requestId });
    }
    await this.geoClient.resendLocationRequest(requestId, contact, requestOptions);
  }

  /**
   * Validate location request input
   */
  private validateLocationRequestInput(input: ComplianceLocationRequestInput): void {
    const errors: string[] = [];

    if (!input.customerId?.trim()) {
      errors.push('customerId is required');
    }

    if (!input.channel) {
      errors.push('channel is required');
    }

    if (!input.reason?.trim()) {
      errors.push('reason is required for compliance/audit purposes');
    }

    // Channel-specific validation
    if (input.channel === 'sms' && !input.phone) {
      errors.push('phone is required for SMS channel');
    }

    if (input.channel === 'email' && !input.email) {
      errors.push('email is required for email channel');
    }

    if (errors.length > 0) {
      throw new ValidationError(`Invalid location request: ${errors.join(', ')}`, errors);
    }
  }

  // ============================================================================
  // Configuration
  // ============================================================================

  /**
   * Update currency exchange rates
   *
   * Use this to keep currency rates current for accurate transaction risk
   * calculation. Rates should be updated regularly (e.g., daily).
   *
   * @param rates - Currency to USD exchange rates
   *
   * @example
   * ```typescript
   * // Update specific rates
   * sdk.updateCurrencyRates({
   *   EUR: 1.08,
   *   GBP: 1.25,
   *   BTC: 42000, // For crypto support
   * });
   *
   * // Or fetch from external service
   * const rates = await fetchExchangeRates();
   * sdk.updateCurrencyRates(rates);
   * ```
   */
  updateCurrencyRates(rates: CurrencyRates): void {
    this.currencyRates = { ...this.currencyRates, ...rates };
    this._currencyRatesCustomized = true;
  }

  /**
   * Get current currency rates
   *
   * @returns Current currency to USD exchange rates
   */
  getCurrencyRates(): Readonly<CurrencyRates> {
    return { ...this.currencyRates };
  }

}
